import { convertFieldType } from '../../lib/convertV2Schema';
import { DataSyncObjectsStore } from '../../stores/object/DataSyncObjectsStore';
import {
  CGBaseTypeDefinition,
  CGBaseTypeFieldDefinition,
  CGContentType,
  CGContentTypeField,
  CGPropertyTypeField,
  CGObjectDefinition,
  OptimizelyProduct,
  CG_BASE_CONTRACT_FIELDS
} from '../../types/DataSyncItem';
import { FieldDefinition, isObjectArray } from '../../types/FieldDefinition';

export class DataSyncObjectDefinitionService {

  /**
   * Filter source fields to exclude system fields that should not be mapped.
   * For CGR sources: excludes _modified, _deleted, and _metadata
   */
  public static filterSourceFields(
    sourceFields: FieldDefinition[],
    sourceProduct: OptimizelyProduct
  ): FieldDefinition[] {
    if (sourceProduct === OptimizelyProduct.CGR) {
      return sourceFields.filter((field) =>
        field.name !== '_modified' && field.name !== '_deleted' && field.name !== '_metadata'
      );
    }
    return sourceFields;
  }

  public static convertToCGObject(
    sourceProduct: OptimizelyProduct,
    sourceStore: DataSyncObjectsStore,
    sourceObjectName: string,
    destinationObjectName: string,
    baseTypeDefinitions: CGBaseTypeDefinition[],
    baseType?: string
  ): CGObjectDefinition {
    const sourceObject = sourceStore.getSourceObjectDefinition(sourceObjectName);
    if (!sourceObject) {
      return {} as CGObjectDefinition;
    }

    const sourceFields = sourceObject.fields || [];

    // Filter source fields to exclude _modified and _deleted fields when source is CGR
    const filteredFields = this.filterSourceFields(sourceFields, sourceProduct);

    // Process base contract fields for graph sources
    const processedFields = this.processSourceFields(filteredFields, sourceProduct, baseType);

    const cgObjectDefinition = {
        object_name: destinationObjectName,
        base_type: baseType,
        fields: processedFields,
        created_by: 'OCP',
        source_object_name: sourceObject.object_name,
        source_product: sourceProduct
    } as CGObjectDefinition;

    if (!baseType) {
      return cgObjectDefinition;
    }
    return this.addRequiredBaseTypeFields(cgObjectDefinition, baseTypeDefinitions, baseType);
  }

  public static convertToCGContentType(cgObjectDefinition: CGObjectDefinition): CGContentType {
    const customTypes = new Map<string, CGPropertyTypeField>();
    const processedFields: CGContentTypeField[] = [];

    // Process fields and extract custom types
    for (const cgfield of cgObjectDefinition.fields.filter((field) => !field.required)) {
      const processedField = this.processFieldForCGContentType(
        cgfield,
        cgObjectDefinition.object_name,
        customTypes
      );
      if (processedField) {
        processedFields.push(processedField);
      }
    }

    const propertyTypes = Array.from(customTypes.values());
    return {
      type_name: cgObjectDefinition.object_name,
      base_types: cgObjectDefinition.base_type ? [cgObjectDefinition.base_type] : [],
      source_product: cgObjectDefinition.source_product,
      source_object_name: cgObjectDefinition.source_object_name,
      created_by: cgObjectDefinition.created_by,
      fields: processedFields,
      property_types: propertyTypes.length > 0 ? propertyTypes : undefined
    } as CGContentType;
  }

  public static getBaseTypeFields(
    baseTypeDefinitions: CGBaseTypeDefinition[],
    baseType: string
  ): CGBaseTypeFieldDefinition[] {
    const baseTypeDefinition = baseTypeDefinitions.find((bt) => bt.name === baseType);
    return baseTypeDefinition ? baseTypeDefinition.fields : [];
  }

  public static addRequiredBaseTypeFields(
    cgObjectDefinition: CGObjectDefinition,
    baseTypeDefinitions: CGBaseTypeDefinition[],
    baseType: string
  ): CGObjectDefinition {
    const requiredFields = this.getBaseTypeFields(baseTypeDefinitions, baseType);
    const cgFields = [...cgObjectDefinition?.fields];

    for (const field of requiredFields) {
      const existingField = cgFields.find((f) => f.name.toLowerCase() === field.name.toLowerCase());
      if (!existingField) {
        cgFields.push({
          name: field.name,
          display_name: field.display_name || field.name,
          type: convertFieldType(field.type),
          required: true,
        } as FieldDefinition);
      } else {
        existingField.required = true;
      }
    }

    return {
      ...cgObjectDefinition,
      fields: cgFields,
    } as CGObjectDefinition;
  }

  public static isCGBaseContractField(sourceProduct: OptimizelyProduct, fieldName: string): boolean {
    return sourceProduct === OptimizelyProduct.CGR &&
      CG_BASE_CONTRACT_FIELDS.some((prefix) => fieldName.startsWith(prefix));
  }

  public static hasCGBaseContractFields(
    fields: FieldDefinition[]
  ): boolean {
    return fields.some((field) => CG_BASE_CONTRACT_FIELDS.some((prefix) => field.name.startsWith(prefix)));
  }

  /**
   * Process a field for CG Content Type creation, handling nested objects and arrays
   */
  private static processFieldForCGContentType(
    field: FieldDefinition,
    contentTypeName: string,
    customTypes: Map<string, CGPropertyTypeField>
  ): CGContentTypeField | null {
    // Handle nested object fields
    if (field.object_schema?.fields) {
      const customTypeName = this.getCustomTypeName(contentTypeName, field);

      // Create custom type for the nested object
      const customTypeProperties: CGContentTypeField[] = [];
      for (const nestedField of field.object_schema.fields) {
        const nestedProcessedField = this.processFieldForCGContentType(
          nestedField,
          contentTypeName,
          customTypes
        );
        if (nestedProcessedField) {
          customTypeProperties.push(nestedProcessedField);
        }
      }

      if (customTypeProperties.length === 0) {
        return null;
      }

      customTypes.set(customTypeName, {
        name: customTypeName,
        fields: customTypeProperties
      });

      return {
        name: field.name,
        type: customTypeName
      };
    }

    // Handle array fields
    if (field.array) {
      if (isObjectArray(field.array)) {
        // Array of objects
        const customTypeName = this.getCustomTypeName(contentTypeName, field);

        // Create custom type for array items
        const customTypeProperties: CGContentTypeField[] = [];
        for (const arrayField of field.array.fields) {
          const arrayProcessedField = this.processFieldForCGContentType(
            arrayField,
            contentTypeName,
            customTypes
          );
          if (arrayProcessedField) {
            customTypeProperties.push(arrayProcessedField);
          }
        }

        if (customTypeProperties.length === 0) {
          return null;
        }

        customTypes.set(customTypeName, {
          name: customTypeName,
          fields: customTypeProperties
        });

        return {
          name: field.name,
          type: `[${customTypeName}]`
        };
      } else {
        // Array of primitives
        const primitiveType = this.mapToCGFieldType(field.array.type);
        return {
          name: field.name,
          type: `[${primitiveType}]`
        };
      }
    }

    // Handle primitive fields
    return {
      name: field.name,
      type: this.mapToCGFieldType(field.type)
    };
  }

  private static processSourceFields(
    sourceFields: FieldDefinition[],
    sourceProduct: OptimizelyProduct,
    baseType?: string
  ): FieldDefinition[] {
    const hasBaseType = !!baseType;
    return sourceFields.filter((field) => {
      return !(this.isCGBaseContractField(sourceProduct, field.name) && hasBaseType);
    }).map((field) => {
      if (this.isCGBaseContractField(sourceProduct, field.name)) {
        const newFieldName = field.name.split('.')[1];
        return {
          ...field,
          name: newFieldName,
          display_name: field.display_name || newFieldName,
          required: false
        };
      }

      return {
        ...field,
        required: false
      };
    });
  }

  private static getCustomTypeName(ContentTypeName: string, field: FieldDefinition): string {
    const customTypeName = field.display_name ? field.display_name.replace(/\s+/g, '') : field.name;
    return `${ContentTypeName}_${customTypeName}`;
  }

  private static mapToCGFieldType(sourceFieldType?: string): string {
    const typeMap: Record<string, string> = {
      'int': 'Int',
      'integer': 'Int',
      'long': 'Int',
      'timestamp': 'DateTime',
      'date': 'Date',
      'datetime': 'DateTime',
      'boolean': 'Boolean',
      'bool': 'Boolean',
      'float': 'Float',
      'double': 'Float',
      'number': 'Float',
      'decimal': 'Float',
      'string': 'String'
    };

    if (!sourceFieldType) {
      return 'String';
    }

    return typeMap[sourceFieldType.toLowerCase()] || 'String';
  }
}
